<template>
  <v-dialog
    v-model="isVisible"
    :fullscreen="isFullScreen"
    height="64"
    max-height="64"
    max-width="850"
    persistent
    scrollable
  >
    <v-card v-if="editItem">
      <v-toolbar
        flat
      >
        <v-avatar
          class="mr-3"
          color="blue"
        >
          <v-icon color="blue lighten-5">
            {{ editItem.Id ? $t('Marketing.Email.Dialog.EditTemplate.Toolbar.Icon') : $t('Marketing.Email.Dialog.AddTemplate.Toolbar.Icon') }}
          </v-icon>
        </v-avatar>


        <v-toolbar-title
          class="pl-1"
        >
          <div class="body-3">
            {{ editItem.Id ? $t('Marketing.Email.Dialog.EditTemplate.Toolbar.Title') : $t('Marketing.Email.Dialog.AddTemplate.Toolbar.Title') }}
          </div>
          <div class="text-caption">
            {{ editItem.Id ? $t('Marketing.Email.Dialog.EditTemplate.Toolbar.SubTitle') : $t('Marketing.Email.Dialog.AddTemplate.Toolbar.SubTitle') }}
          </div>
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          class="ml-0"
          icon
          @click="onCancelButtonClick"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text class="pa-2 fill-height">
        <v-form
          ref="dataForm"
          v-model="dataFormValid"
          @submit.prevent
        >
          <v-container
            class="pa-0"
            fluid
          >
            <v-row dense>
              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="editItem.Title"
                  :hint="$t('Marketing.Email.Dialog.EditTemplate.Field.Title.Hint')"
                  :label="$t('Marketing.Email.Dialog.EditTemplate.Field.Title.Label')"
                  :prepend-icon="$t('Marketing.Email.Dialog.EditTemplate.Field.Title.Icon')"
                  :rules="formFieldRules.Title"
                  filled
                  hide-details="auto"
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="editItem.Subject"
                  :hint="$t('Marketing.Email.Dialog.EditTemplate.Field.Subject.Hint')"
                  :label="$t('Marketing.Email.Dialog.EditTemplate.Field.Subject.Label')"
                  :prepend-icon="$t('Marketing.Email.Dialog.EditTemplate.Field.Subject.Icon')"
                  :rules="formFieldRules.Subject"
                  filled
                  hide-details="auto"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="isVisible"
              dense
            >
              <v-col cols="12">
                <unlayer-editor
                  ref="editor"
                  :appearance="editor.appearance"
                  :editor-id="editor.id"
                  :min-height="editor.minHeight"
                  :options="editor.options"
                  :project-id="editor.projectId"
                  :tools="editor.tools"
                  @load="editorLoaded"
                  @ready="editorReady"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-2">
        <v-spacer />

        <v-btn
          class="light-blue--text text--darken-1"
          outlined
          @click="onCancelButtonClick"
        >
          {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
        </v-btn>

        <v-btn
          :disabled="formIsSaving || !editorIsReady"
          :loading="formIsSaving"
          color="green"
          dark
          depressed
          @click="onSaveButtonClick"
        >
          {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Auth from '@/mixins/auth'
import DialogVisible from '@/mixins/dialogVisible'
import {clone} from '@/lib/helper/helper.js'
import UnlayerEditor from '@/components/common/unlayer-editor/UnlayerEditor.vue'

export default {
  name      : 'EmailTemplateEditDialog',
  components: {UnlayerEditor},
  mixins    : [Auth, DialogVisible],
  props     : {
    template: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      isFullScreen  : true,
      dataFormValid : false,
      editItem      : null,
      defaultDataObj: {
        Id      : null,
        Title   : '',
        Subject : '',
        Body    : '',
        BodyText: '',
        Source  : '',
        Type    : 'email'
      },
      editorIsReady: false,
      editor       : {
        id       : 'email-template-editor-id',
        //projectId: 0,
        locale   : 'en-US',
        minHeight: '745px',
        tools    : {},
        options  : {
          version    : 'stable', // Specify 'latest', 'stable' or a specific version number
          displayMode: 'email', // email, web, popup, document
          features   : {
            textEditor: {
              tables: true, // Enable tables in the text editor
              emojis: true // Enable emoji support in the text editor
            }
          },
          mergeTags: {
            customer: {
              name     : 'Customer',
              mergeTags: {
                first_name: {
                  name  : 'First Name',
                  value : '{{first_name}}',
                  sample: 'John'
                },
                last_name: {
                  name  : 'Last Name',
                  value : '{{last_name}}',
                  sample: 'Doe'
                },
                email: {
                  name  : 'Email',
                  value : '{{email}}',
                  sample: 'test@test.com'
                }
              }
            }
          }
        }
      },
      formIsSaving  : false,
      formFieldRules: {
        Title: [
          (v) => !!v || this.$t('Marketing.Email.Dialog.EditTemplate.Field.Title.Error.Required'),
          (v) => v && v.length >= 2 && v.length <= 255 || this.$t('Marketing.Email.Dialog.EditTemplate.Field.Title.Error.Between')
        ],
        Subject: [
          (v) => !!v || this.$t('Marketing.Email.Dialog.EditTemplate.Field.Subject.Error.Required'),
          (v) => v && v.length >= 2 && v.length <= 255 || this.$t('Marketing.Email.Dialog.EditTemplate.Field.Subject.Error.Between')
        ]
      }
    }
  },
  computed: {},
  watch   : {
    isVisible(newVal) {
      if (newVal) {
        this.initDialog()
      } else {
        this.destroyDialog()
      }
    }
  },
  created() {
    this.initDialog()
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.destroyDialog()
  },
  methods: {
    initDialog() {
      this.editItem = clone(this.template || this.defaultDataObj)
      this.editorIsReady = false
      this.initEvents()
    },
    destroyDialog() {
      this.editItem = null
      this.editorIsReady = false
      this.destroyEvents()
    },
    initEvents() {
      this.destroyEvents()
      this.$bus.$on(window.SocketCommand.Marketing.Email.Template.Save, this.onSaveResult)
    },
    destroyEvents() {
      this.$bus.$off(window.SocketCommand.Marketing.Email.Template.Save, this.onSaveResult)
    },

    editorReady() {
      this.editorIsReady = true
    },

    editorLoaded() {
      if (this.editItem?.Source) {
        this.$refs.editor.editor.loadDesign(this.editItem.Source)
      }
    },

    onSaveButtonClick() {
      if (!this.$refs.dataForm.validate()) return

      this.formIsSaving = true
      const payload = clone(this.editItem)

      this.$refs.editor.editor.exportHtml(
        (data) => {
          payload.Source = data?.design || null
          payload.Chunks = data?.chunks || null
          payload.Body = this.htmlDecode(data?.html || '')

          this.$refs.editor.editor.exportPlainText(
            (data) => {
              payload.BodyText = data?.text || ''

              window.callAS(window.SocketCommand.Marketing.Email.Template.Save, payload)
            }
          )
        },
        {
          cleanup     : true,
          inlineStyles: true,
          minify      : true
        }
      )
    },

    onSaveResult(data) {
      this.formIsSaving = false

      if (data && data.status === 'success') {
        if (data?.Id) this.editItem.Id = data.Id
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Marketing.Email.Dialog.EditTemplate.Message.Save.Success'),
          type: 'success',
          icon: 'info'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Error.Generic'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    htmlDecode(input) {
      const doc = new DOMParser().parseFromString(input, 'text/html')
      return doc.documentElement.innerHTML
    },

    onCancelButtonClick() {
      this.closeDialog()
    }
  }
}
</script>
